/**
 * Builds the printable form of the LawFusion Summary.
 *
 * The download is meant to be the summary the visitor just read, not a plain
 * transcript of it: same order, same headings, same coloured blocks. So this
 * mirrors ConsumerSummary's markup with inline styles.
 *
 * The styles are inline because the node this renders from is attached to
 * document.body, off-screen — not detached — so every global rule in the app
 * still applies to it. Anything left to inherit is fair game for a blanket
 * selector in index.css; see RICH_TEXT_CSS.
 *
 * Colours are the light-mode tokens from LegalQuestion.css. Paper is white
 * whatever the visitor's theme is — the dark palette is for screens.
 *
 * Pass the result to handleDownloadPDF with `{ styled: true }`, which skips
 * the document stylesheet that would otherwise flatten all of this back to
 * black-on-white serif.
 */
import { parseConsumerSummary, SUMMARY_BLOCKS } from "./consumerSummary";
import { convertTextToHtml } from "./textProcessing";

/** Matches .lq-block-<tone> in LegalQuestion.css. */
const TONES = {
  blue: { bg: "#eff6ff", border: "#bfdbfe", chip: "#dbeafe", ink: "#1d4ed8" },
  green: { bg: "#f0fdf4", border: "#bbf7d0", chip: "#dcfce7", ink: "#15803d" },
  amber: { bg: "#fff7ed", border: "#fed7aa", chip: "#ffedd5", ink: "#c2410c" },
  teal: { bg: "#f0fdfa", border: "#99f6e4", chip: "#ccfbf1", ink: "#0f766e" },
  violet: { bg: "#faf5ff", border: "#e9d5ff", chip: "#ede9fe", ink: "#6d28d9" },
};

/**
 * Lucide paths for the icons ConsumerSummary renders, as raw markup — the
 * React components cannot be used in a string of HTML. Kept to the same five
 * glyphs so the printed block is recognisably the one on screen.
 */
const ICON_PATHS = {
  bottomLine:
    '<path d="M2.7 10.3a2.41 2.41 0 0 0 0 3.41l7.59 7.59a2.41 2.41 0 0 0 3.41 0l7.59-7.59a2.41 2.41 0 0 0 0-3.41L13.7 2.71a2.41 2.41 0 0 0-3.41 0Z"/>',
  agree: '<path d="M20 6 9 17l-5-5"/>',
  depends:
    '<circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><path d="M12 17h.01"/>',
  options: '<path d="M5 12h14"/><path d="m12 5 7 7-7 7"/>',
  attorneyQuestions:
    '<path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>',
};

const escapeHtml = (text = "") =>
  String(text)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

/**
 * Explicit stroke rather than currentColor: html2canvas serialises the SVG on
 * its own and does not resolve inherited colour when it does.
 */
const icon = (key, color) => {
  const paths = ICON_PATHS[key];
  if (!paths) return "";
  return (
    `<svg width="19" height="19" viewBox="0 0 24 24" fill="none" ` +
    `stroke="${color}" stroke-width="2" stroke-linecap="round" ` +
    `stroke-linejoin="round">${paths}</svg>`
  );
};

/**
 * Rules for the model-authored HTML inside each block, which arrives from
 * convertTextToHtml and so cannot carry inline styles of its own. Mirrors
 * .lq-rich. Safe to inject as a <style> element: the node is torn down as
 * soon as the PDF is rasterised.
 *
 * Every colour is forced per element rather than inherited from .lfx-rich,
 * and that is not belt-and-braces. handleDownloadPDF renders from a node
 * attached to document.body, so index.css's blanket dark-mode rules
 * (`body.dark p, body.dark span, body.dark div { color: var(--text-primary) }`)
 * match this content directly — and a direct match beats an inherited colour
 * however specific the ancestor rule is. A visitor in dark mode got a page of
 * near-white text on white paper. Paper is white whatever the theme, so the
 * colours here are absolute.
 */
const RICH_TEXT_CSS = `
  .lfx-rich { font-size: 10.5pt; line-height: 1.6; }
  .lfx-rich,
  .lfx-rich p,
  .lfx-rich div,
  .lfx-rich span,
  .lfx-rich ul,
  .lfx-rich ol,
  .lfx-rich li,
  .lfx-rich td,
  .lfx-rich th,
  .lfx-rich blockquote,
  .lfx-rich em,
  .lfx-rich i,
  .lfx-rich code,
  .lfx-rich pre { color: #334155 !important; }
  .lfx-rich > :first-child { margin-top: 0; }
  .lfx-rich > :last-child { margin-bottom: 0; }
  .lfx-rich p { margin: 0 0 9px; }
  .lfx-rich ul, .lfx-rich ol { margin: 5px 0 9px; padding-left: 20px; }
  .lfx-rich li { margin: 0 0 5px; }
  .lfx-rich h1, .lfx-rich h2, .lfx-rich h3,
  .lfx-rich h4, .lfx-rich h5, .lfx-rich h6 {
    margin: 13px 0 6px;
    font-size: 11pt;
    font-weight: 700;
    color: #0f172a !important;
  }
  .lfx-rich strong, .lfx-rich b { color: #0f172a !important; font-weight: 700; }
  .lfx-rich a { color: #1d4ed8 !important; text-decoration: underline; }
`;

const header = (modelsText) => {
  const sub = modelsText
    ? `<div style="font-size:10pt;color:#64748b;margin-top:4px;">Based on <strong style="color:#1d4ed8;font-weight:700;">${escapeHtml(
        modelsText,
      )}</strong></div>`
    : "";
  return (
    `<div style="margin:0 0 20px;padding:0 0 14px;` +
    `border-bottom:1px solid #e2e8f0;">` +
    `<div style="font-size:19pt;font-weight:800;letter-spacing:-0.5px;` +
    `color:#0f172a;">Your LawFusion Summary</div>${sub}</div>`
  );
};

/** One coloured block. Flex, like the page — html2canvas lays it out from the real box positions. */
const block = ({ tone, title, key }, bodyHtml, isFirst) => {
  const t = TONES[tone] || TONES.blue;
  return (
    `<div style="display:flex;gap:14px;margin-top:${isFirst ? 0 : 12}px;` +
    `padding:18px;border-radius:14px;background:${t.bg};` +
    `border:1px solid ${t.border};">` +
    `<div style="width:36px;height:36px;min-width:36px;border-radius:50%;` +
    `background:${t.chip};display:flex;align-items:center;` +
    `justify-content:center;">${icon(key, t.ink)}</div>` +
    `<div style="flex:1;min-width:0;">` +
    `<div style="margin:0 0 6px;font-size:12pt;font-weight:700;` +
    `color:${t.ink};">${escapeHtml(title)}</div>` +
    `<div class="lfx-rich">${bodyHtml}</div>` +
    `</div></div>`
  );
};

const disclaimerHtml = (text) =>
  `<p style="margin:16px 0 0;font-size:8.5pt;line-height:1.55;` +
  `color:#94a3b8;">${escapeHtml(text)}</p>`;

/**
 * @param {object} args
 * @param {string} args.summary - raw summary text as the model wrote it
 * @param {string} [args.formatted] - the visitor's edited HTML, if they edited
 * @param {string} [args.modelsText] - "ChatGPT, Claude and Gemini"
 * @returns {string} HTML for handleDownloadPDF(..., { styled: true })
 */
export function buildSummaryExportHtml({
  summary = "",
  formatted = "",
  modelsText = "",
}) {
  const shell = (body) =>
    `<style>${RICH_TEXT_CSS}</style>` +
    `<div style="background:#ffffff;color:#1e293b;">` +
    `${header(modelsText)}${body}</div>`;

  // An edited summary is the visitor's own document — re-deriving the blocks
  // would throw away what they wrote. Same rule the screen follows.
  if (formatted.trim()) {
    return shell(`<div class="lfx-rich">${formatted}</div>`);
  }

  const parsed = parseConsumerSummary(summary);

  // Unrecognised structure — print the model's own text rather than nothing,
  // again matching what the page shows.
  if (!parsed.matched) {
    return shell(`<div class="lfx-rich">${convertTextToHtml(summary)}</div>`);
  }

  const parts = [];

  if (parsed.preamble) {
    parts.push(
      `<div class="lfx-rich" style="margin-bottom:14px;">${convertTextToHtml(
        parsed.preamble,
      )}</div>`,
    );
  }

  let first = true;
  for (const meta of SUMMARY_BLOCKS) {
    const body = parsed.sections[meta.key];
    if (!body) continue;
    parts.push(block(meta, convertTextToHtml(body), first && !parsed.preamble));
    first = false;
  }

  if (parsed.disclaimer) parts.push(disclaimerHtml(parsed.disclaimer));

  return shell(parts.join(""));
}
